import React, { useState, useEffect, useRef } from 'react';

const Slideshow = ({ currentLanguage = 'EN' }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const slides = [
    {
      id: 1,
      image: "/slideshow/computer1.png",
      fallbackImage: "https://images.unsplash.com/photo-1633158829585-23ba8f7c8faf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      title: "Luxury Hair Styling",
      subtitle: "Experience the Art of Beauty",
      description: "Transform your look with our expert stylists and premium treatments",
      buttonText: "Book Appointment"
    },
    {
      id: 2,
      image: "/slideshow/computer2.png",
      fallbackImage: "https://images.unsplash.com/photo-1519669556878-63bdad8a1e45?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      title: "Professional Spa Services",
      subtitle: "Relax and Rejuvenate",
      description: "Indulge in our luxurious spa treatments for complete relaxation",
      buttonText: "View Services"
    }
  ];

  const currentSlides = slides;

  const handleImageError = (slideId) => {
    setImageErrors(prev => ({ ...prev, [slideId]: true }));
  };

  const getImageUrl = (slide) => {
    return imageErrors[slide.id] ? slide.fallbackImage : slide.image;
  };

  const startAutoPlay = () => {
    if (isAutoPlaying && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
      }, 5000);
    }
  };

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(prev => !prev);
    if (!isAutoPlaying) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }
  };

  useEffect(() => {
    startAutoPlay();

    return () => stopAutoPlay();
  }, [currentSlides.length]);

  // Pause auto-play on hover
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleMouseEnter = () => setIsAutoPlaying(false);
      const handleMouseLeave = () => setIsAutoPlaying(true);

      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
    startAutoPlay();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + currentSlides.length) % currentSlides.length);
    startAutoPlay();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    startAutoPlay();
  };

  // Mouse drag handlers for desktop
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    stopAutoPlay();
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    startAutoPlay();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust scroll speed
    
    // Determine if it's a significant drag to change slide
    if (Math.abs(walk) > 50) {
      if (walk > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
      setIsDragging(false);
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    stopAutoPlay();
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        nextSlide(); // Swipe left -> next
      } else {
        prevSlide(); // Swipe right -> prev
      }
    }
    startAutoPlay();
  };

  return (
    <>
      <style>{`
        .font-inter {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .slideshow-container {
          cursor: grab;
        }
        .slideshow-container:active {
          cursor: grabbing;
        }
      `}</style>

      <div 
        className="slideshow-container relative overflow-hidden bg-white shadow-md mx-auto max-w-6xl"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {/* Slides - Using translateX for horizontal sliding to enable "scroll" feel */}
        <div 
          className="relative flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentSlides.map((slide, index) => (
            <div key={slide.id} className="w-full h-full flex-shrink-0 relative">
              {/* Background Image */}
              <div className="relative w-full h-full">
                <img 
                  src={getImageUrl(slide)} 
                  alt={slide.title}
                  className="w-full h-full "
                  onError={() => handleImageError(slide.id)}
                />
                <div className="absolute inset-0 "></div> {/* Optional overlay for better text readability */}
              </div>

              
            </div>
          ))}
        </div>

        
      </div>
    </>
  );
};

export default Slideshow;